/**
 * The bench inspector (backlog B-11) — selecting a benched tower fills the side panel
 * with the same reading the SHOP gives: identity, stats, and above all the placement
 * preview ("+1 Frost → tier 4"), because the question while holding a tower is exactly
 * the question while buying one: what does putting this down do to my board?
 *
 * The deltas arrive on the snapshot computed by the sim (the same `previewPlacement`
 * the shop cards use), and the sell refund is the sim's exact number — nothing here is
 * recomputed (TRD-09 §2).
 */
import { getTower } from '@prism/content';
import type { ClassId, UiSnapshot } from '@prism/sim';
import { ClassChip } from './ShopCard.js';
import { formatInterval, formatRange, previewLines } from './shopCardModel.js';

type BenchItem = NonNullable<UiSnapshot['bench'][number]>;

interface BenchInspectorProps {
  item: BenchItem;
  onSell: () => void;
}

export function BenchInspector({ item, onSell }: BenchInspectorProps) {
  const def = getTower(item.defId);
  // Star scaling multiplies damage only (TRD-07 §7) — interval and range are printed.
  const scale = def.starScaling[item.star - 1] ?? 1;
  const damage = Math.round(def.base.damage * scale);
  const lines = previewLines(item.deltas);

  return (
    <div className="bench-inspector">
      <div className="inspector__head">
        <b>
          {def.name}
          {item.star > 1 ? ` ★${item.star}` : ''}
        </b>
        {def.classes.map((classId) => (
          <ClassChip key={classId} classId={classId as ClassId} />
        ))}
      </div>

      {def.support ? (
        <span className="muted">SUPPORT · never attacks · {formatRange(def.base.range)} aura</span>
      ) : (
        <span className="muted">
          {damage} dmg{def.base.projectile.kind === 'zone' ? '/strike' : ''} ·{' '}
          {formatInterval(def.base.attackInterval)} · {formatRange(def.base.range)} rng
        </span>
      )}

      {(def.support || def.innateEffects.length > 0 || def.base.projectile.kind === 'zone') && (
        <span className="muted shop-card__desc">{def.description}</span>
      )}

      {lines.map((line) => (
        <span
          key={line.classId}
          className={`shop-card__preview${line.crossesThreshold ? ' shop-card__preview--unlock' : ''}`}
        >
          {line.text}
          {line.unlocked && <span className="shop-card__unlock-text"> {line.unlocked}</span>}
        </span>
      ))}

      <p className="muted bench-inspector__hint">Click a tile to place · right-click cancels</p>

      <button type="button" className="gold" title="Sell this bench tower" onClick={onSell}>
        Sell +{item.sellRefund}g
      </button>
    </div>
  );
}
